// src/pages/admin/AdminDashboard.js
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import AdminLayout from "../../components/admin/AdminLayout";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "https://crm-backend-5tk6.onrender.com";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
`;

const IconCircle = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${p => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  h3 { margin: 0; font-size: 1.6rem; color: #222; }
  p { margin: 0; font-size: 0.8rem; color: #888; }
  a { font-size: 0.75rem; color: #c8102e; text-decoration: none; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  h3 { margin: 0 0 16px; font-size: 0.95rem; color: #333; }
`;

const AccesoBtn = styled.div`
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  text-align: center;
  font-size: 0.85rem;
  color: #444;
  &:hover { border-color: #c8102e; color: #c8102e; background: #fff5f5; }
  .icon { font-size: 1.6rem; margin-bottom: 6px; }
  p { margin: 4px 0 0; font-size: 0.75rem; color: #999; }
`;

const AccesosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`;

const LogItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
`;

const LogDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.color || "#eee"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
`;

const LogInfo = styled.div`
  flex: 1;
  p { margin: 0; font-size: 0.82rem; color: #333; font-weight: 500; }
  span { font-size: 0.75rem; color: #999; }
`;

const LogHora = styled.div`
  font-size: 0.75rem;
  color: #aaa;
  white-space: nowrap;
`;

const TIPOS_CONFIG = [
  { tipo: "CECAP", color: "#fdecea", icon: "📋", iconColor: "#FFCCBC" },
  { tipo: "FONE",  color: "#e3f2fd", icon: "🚒", iconColor: "#BBDEFB" },
  { tipo: "APS",   color: "#e8f5e9", icon: "🏥", iconColor: "#C8E6C9" },
  { tipo: "CEC",   color: "#fff8e1", icon: "📜", iconColor: "#FFF9C4" },
  { tipo: "CENCAD",color: "#f3e5f5", icon: "🎓", iconColor: "#E1BEE7" },
];

const ACCION_CONFIG = {
  CREAR:        { icon: "➕", color: "#4caf50" },
  EDITAR:       { icon: "✏️", color: "#2196f3" },
  ELIMINAR:     { icon: "🗑️", color: "#f44336" },
  CARGA_MASIVA: { icon: "📤", color: "#ff9800" },
  FOTO_SUBIDA:  { icon: "🖼️", color: "#9c27b0" },
};

export default function AdminDashboard() {
  const [conteos, setConteos] = useState({});
  const [actividad, setActividad] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    fetch(`${API_URL}/api/admin/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setConteos(data.conteos || {});
        setActividad(data.actividad || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatHora = (fecha) => {
    if (!fecha) return "";
    return new Date(fecha).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <AdminLayout titulo="Dashboard">
      {loading ? (
        <p style={{ color: "#888" }}>Cargando...</p>
      ) : (
        <>
          <Grid>
            {TIPOS_CONFIG.map(({ tipo, color, icon, iconColor }) => (
              <Card key={tipo} onClick={() => navigate(`/admin/certificados/${tipo}`)}>
                <IconCircle color={iconColor}>{icon}</IconCircle>
                <CardInfo>
                  <h3>{(conteos[`certificados${tipo}`] || 0).toLocaleString()}</h3>
                  <p>{tipo}</p>
                  <a href={`/admin/certificados/${tipo}`}>Ver detalles →</a>
                </CardInfo>
              </Card>
            ))}
          </Grid>

          <Row>
            <Panel>
              <h3>Accesos rápidos</h3>
              <AccesosGrid>
                <AccesoBtn onClick={() => navigate("/admin/certificados/CECAP")}>
                  <div className="icon">📋</div>
                  Gestionar certificados
                  <p>Ver, crear, editar y eliminar</p>
                </AccesoBtn>
                <AccesoBtn onClick={() => navigate("/admin/carga-masiva")}>
                  <div className="icon">📊</div>
                  Carga masiva (Excel)
                  <p>Importar desde archivo</p>
                </AccesoBtn>
                <AccesoBtn onClick={() => navigate("/admin/fotos")}>
                  <div className="icon">🖼️</div>
                  Carga de fotos
                  <p>Subir imágenes PNG</p>
                </AccesoBtn>
                <AccesoBtn onClick={() => navigate("/admin/log")}>
                  <div className="icon">🕐</div>
                  Log de actividad
                  <p>Historial de acciones</p>
                </AccesoBtn>
                <AccesoBtn onClick={() => navigate("/admin/carga-masiva")}>
                  <div className="icon">⬇️</div>
                  Descargar machotes
                  <p>Formatos Excel por tipo</p>
                </AccesoBtn>
              </AccesosGrid>
            </Panel>

            <Panel>
              <h3>Actividad reciente</h3>
              {actividad.length === 0 && <p style={{ color: "#aaa", fontSize: "0.85rem" }}>Sin actividad registrada.</p>}
              {actividad.map((item, i) => {
                const cfg = ACCION_CONFIG[item.accion] || { icon: "•", color: "#ccc" };
                return (
                  <LogItem key={i}>
                    <LogDot color={cfg.color + "22"}>{cfg.icon}</LogDot>
                    <LogInfo>
                      <p>{item.accion.replace("_", " ")} — {item.tipo}</p>
                      <span>{item.referencia}</span>
                    </LogInfo>
                    <LogHora>{formatHora(item.fecha)}</LogHora>
                  </LogItem>
                );
              })}
            </Panel>
          </Row>
        </>
      )}
    </AdminLayout>
  );
}
